import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { apiClient } from '../../../lib/api';
import { AgentUsageRollup, UsageTotals, WorkspaceUsage } from '../../../types/agentUsage';
import { useToastContext } from '../../../contexts/ToastContext';
import { ERROR_MESSAGES } from '../../../constants/error-messages';
import {
  TABLE_TD as TD,
  TABLE_TH as TH,
  failureRatePct,
  formatCost,
  formatDuration,
} from '../../../utils/agentUsageUtils';
import { Badge, Spinner, WindowToggle } from '../../ui';

/** Admin per-client agent usage over a window, with per-project drill-down. */

const COLUMNS = ['Client', 'Runs', 'Outcome', 'Tokens in / out', 'Cost', 'Avg duration'];

/** One row: a label cell plus the shared metric cells. */
const Row: React.FC<{ label: React.ReactNode; t: UsageTotals; indent?: boolean }> = ({
  label,
  t,
  indent,
}) => (
  <tr className={indent ? 'bg-neutral-50' : 'hover:bg-neutral-50'}>
    <td className={`${TD} ${indent ? 'pl-12 text-neutral-600' : ''}`}>{label}</td>
    <td className={`${TD} tabular-nums`}>{t.runs.toLocaleString()}</td>
    <td className={TD}>
      <span className="flex items-center gap-2">
        <span className="text-neutral-700 tabular-nums">{t.succeeded.toLocaleString()} ok</span>
        {t.failed > 0 && (
          <Badge variant={failureRatePct(t) >= 20 ? 'error' : 'warning'} size="sm">
            {t.failed} failed · {failureRatePct(t)}%
          </Badge>
        )}
      </span>
    </td>
    <td className={`${TD} tabular-nums text-neutral-600`}>
      {t.tokens_in.toLocaleString()} / {t.tokens_out.toLocaleString()}
    </td>
    <td className={`${TD} tabular-nums font-medium`}>{formatCost(t.cost_usd)}</td>
    <td className={`${TD} tabular-nums text-neutral-600`}>{formatDuration(t.avg_duration_secs)}</td>
  </tr>
);

/** Two tbodies per client so the project rows are one addressable element for
 *  aria-controls — a group of <tr>s has no other legal wrapper. */
const ClientRows: React.FC<{ ws: WorkspaceUsage }> = ({ ws }) => {
  const [expanded, setExpanded] = useState(false);
  const panelId = `agent-usage-projects-${ws.workspace_id}`;
  const Chevron = expanded ? ChevronDown : ChevronRight;
  return (
    <>
      <tbody className="border-t border-neutral-200">
        <Row
          t={ws}
          label={
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls={panelId}
              className="flex items-center gap-1.5 font-medium text-neutral-900 hover:text-primary-700 rounded focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10"
            >
              <Chevron className="w-4 h-4 text-neutral-400" aria-hidden />
              {ws.workspace_name ?? ws.workspace_id}
            </button>
          }
        />
      </tbody>
      <tbody id={panelId} className={expanded ? '' : 'hidden'}>
        {ws.projects.map((p) => (
          <Row
            key={p.gtm_project_id ?? 'none'}
            t={p}
            indent
            // Unattributed bucket has null id AND null name (review #629 P1) —
            // the label must never render blank.
            label={p.project_name ?? p.gtm_project_id ?? '(no project)'}
          />
        ))}
      </tbody>
    </>
  );
};

export const AgentUsageTab: React.FC = () => {
  const { showToast } = useToastContext();
  const [days, setDays] = useState(7);
  const [rollup, setRollup] = useState<AgentUsageRollup | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Effect-scoped cancellation (review #629 P1): a slow 7d response must not
    // land after a 30d switch and mislabel the table, nor set state post-unmount.
    let cancelled = false;
    setIsLoading(true);
    apiClient
      .getAdminAgentUsage(days)
      .then((res) => {
        if (cancelled) return;
        setRollup(res);
        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to load agent usage rollup:', error);
        showToast(ERROR_MESSAGES.AGENT_USAGE_LOAD_FAILED, 'error');
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, showToast]);

  const workspaces = rollup?.workspaces ?? [];

  return (
    <section id="agent-usage-tab" aria-labelledby="agent-usage-heading">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 id="agent-usage-heading" className="text-base font-semibold text-neutral-900">
            Agent usage by client
          </h3>
          <p className="text-13 text-neutral-500">
            Runs, tokens, cost and failure rate per workspace — expand a row for projects.
          </p>
        </div>
        <WindowToggle days={days} onChange={setDays} />
      </div>

      {rollup?.truncated && (
        <p className="mb-3 text-xs text-warning-700 bg-warning-50 border border-warning-200 rounded px-3 py-2">
          Row cap reached for this window — totals below are a floor, not the full window.
        </p>
      )}

      {isLoading ? (
        <Spinner size="md" text="Loading agent usage..." center />
      ) : workspaces.length === 0 ? (
        <p className="text-sm text-neutral-500 py-8 text-center">
          No agent runs in the last {days} days.
        </p>
      ) : (
        <div className="overflow-x-auto border border-neutral-200 rounded-md bg-white">
          <table className="min-w-full" aria-label="Agent usage by client">
            <thead className="bg-neutral-50">
              <tr>
                {COLUMNS.map((h) => (
                  <th key={h} scope="col" className={TH}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            {workspaces.map((ws) => (
              <ClientRows key={ws.workspace_id} ws={ws} />
            ))}
          </table>
        </div>
      )}
    </section>
  );
};
